import { useState, useEffect } from 'react';
import { VerticalLayout } from '../../common/common-layout';
import MainLayout from '../MainLayout';
import { observer } from 'mobx-react-lite';
import { TitleLargeMedium } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import BTList from '../../common/components/board/BTList';
import { GridRowParams } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminAccount from '../../model/auth/AdminAccount';
import { formatDateToYMD } from '../../common/utils/DateUtils';
import theme from '../../common/theme';
import EditIcon from '../../assets/icons/edit.svg';
import AuthEditIcon from '../../assets/icons/auth_edit.svg';
import { dialogStore } from '../../store/DialogStore';
import { baseStore } from '../../store/BaseStore';
import { getAllAdminAccounts, requestDeleteAdminAccount, requestToggleSuperUser } from '../../service/AccountService';

const AccountMain = () => {
    return (
        <MainLayout>
            <AdminAccountBody />
        </MainLayout>
    );
};

export default AccountMain;

const AdminAccountBody = observer(() => {
    const navigate = useNavigate();
    const [adminAccounts, setAdminAccounts] = useState<AdminAccount[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    const loadData = async () => {
        baseStore.showLoading();
        const fetchedAdminAccounts = await getAllAdminAccounts();
        setAdminAccounts(fetchedAdminAccounts);
        baseStore.hideLoading();
    };

    useEffect(() => {
        loadData();
    }, []);    

    const handlePasswordChange = (username: string, email: string) => {
        navigate("/account/change-password", {
            state: { username: username, email: email }
        });
        
    };

    const handleToggleSuperUser = async (username: string, email: string, currentIsSuperUser: boolean | null) => {
        // null 값을 false로 처리
        const isCurrentlySuperUser = currentIsSuperUser === true;
        const action = isCurrentlySuperUser ? '회수' : '부여';
        
        const confirmed = await dialogStore.showAsyncDialog({
            title: '확인',
            message: `${username}에게 슈퍼 관리자 권한을 ${action}하시겠습니까?`,
            isCaution: false
        });

        if (!confirmed) {
            return;
        }

        try {
            baseStore.showLoading();
            await requestToggleSuperUser(email, username, !isCurrentlySuperUser);
            
            dialogStore.showDialog({
                title: '알림',
                message: `슈퍼 관리자 권한 ${action}이 완료되었습니다.`,
                onConfirm: () => {
                    console.log('확인');
                    loadData();
                },
                onClose: () => {
                    console.log('닫기');
                    loadData();
                },
            });
        } catch (error) {
            console.error("슈퍼 관리자 권한 변경 실패:", error);
            dialogStore.showDialog({
                title: '오류',
                message: `슈퍼 관리자 권한 ${action}에 실패했습니다.`,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
        } finally {
            baseStore.hideLoading();
        }
    };

    const columns = [
        { field: 'id', headerName: '순번', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'username', headerName: '아이디', width: 200, headerAlign: 'center', align: 'center' },
        { field: 'email', headerName: '이메일', flex: 1, headerAlign: 'center', align: 'center' },
        { field: 'createdDate', headerName: '생성일', width: 200, headerAlign: 'center', align: 'center' },
        { field: 'isSuperUser', headerName: '슈퍼 관리자 여부', width: 140, headerAlign: 'center', align: 'center' },
        {
            field: 'permissionChange', headerName: '관리자 권한 변경', width: 150, headerAlign: 'center', align: 'center', renderCell: (params: any) => (
                <Button
                    variant="contained"
                    startIcon={<img src={AuthEditIcon} />}
                    sx={{
                        backgroundColor: '#E9ECEF',
                        color: theme.colors.textCancel,
                        fontSize: '16px', // 글씨 크기
                        boxShadow: 'none', // 그림자 제거
                        width: '80px', // 가로 크기
                        height: '40px', // 세로 크기
                        fontWeight: '550',
                        '&:focus': {
                            outline: 'none', // focus 시 테두리 제거
                        },
                        '&:active': {
                            outline: 'none', // active 시 테두리 제거
                        }
                    }}
                    onClick={() => handleToggleSuperUser(params.row.username, params.row.email, params.row.isSuperUserValue)}
                >
                    변경
                </Button>
            ),
        },
        {
            field: 'passwordChange', headerName: '비밀번호 변경', width: 150, headerAlign: 'center', align: 'center', renderCell: (params: any) => (
                <Button
                    variant="contained"
                    startIcon={<img src={EditIcon} />}
                    sx={{
                        backgroundColor: '#E9ECEF',
                        color: theme.colors.textCancel,
                        fontSize: '16px', // 글씨 크기
                        boxShadow: 'none', // 그림자 제거
                        width: '80px', // 가로 크기
                        height: '40px', // 세로 크기
                        fontWeight: '550',
                        '&:focus': {
                            outline: 'none', // focus 시 테두리 제거
                        },
                        '&:active': {
                            outline: 'none', // active 시 테두리 제거
                        }
                    }}
                    onClick={() => handlePasswordChange(params.row.username, params.row.email)} // 행의 id를 전달하여 처리
                >
                    변경
                </Button>
            ),
        },
    ];

    const rows = adminAccounts.map((account, index) => ({
        id: index + 1,
        username: account.username,
        email: account.email,
        isSuperUser: account.isSuperUser ? '✅' : '❌',
        isSuperUserValue: account.isSuperUser, // 원본 boolean 값 보존
        createdDate: formatDateToYMD(account.createdAt)
    }));

    const handleRowClick = (params: GridRowParams): void => {

    };

    const handleWriteClick = (): void => {
        navigate("/account/add")
    };

    const handleDeleteClick = async (selectedIndexes: number[]) => {
        if (selectedIndexes.length === 0) {
            dialogStore.showDialog({
                title: '알림',
                message: '삭제할 계정을 선택해주세요.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });

            return;
        } else if (selectedIndexes.length > 1) {
            dialogStore.showDialog({
                title: '알림',
                message: '한 번에 하나의 계정만 삭제할 수 있습니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });

            return;
        }

        const selectedAccount = adminAccounts[selectedIndexes[0]];
        const deleteConfirmed = await dialogStore.showAsyncDialog({
            title: '경고',
            message: `계정 ${selectedAccount.username}을 삭제하시겠습니까?`,
            isCaution: true
        });

        if (!deleteConfirmed) {
            return;
        }

        try {
            await requestDeleteAdminAccount(selectedAccount.email);

            dialogStore.showDialog({
                title: '주의',
                message: `계정 ${selectedAccount.username} 삭제에 성공하였습니다.`,
                onConfirm: () => {
                    console.log('확인');
                    setSelectedRows([]);
                    loadData();
                },
                onClose: () => {
                    console.log('닫기');
                    setSelectedRows([]);
                    loadData();
                },
            });

        } catch (e) {
            dialogStore.showDialog({
                title: '주의',
                message: `계정 ${selectedAccount.username} 삭제에 실패하였습니다.`,
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
        }
    };

    return (
        <VerticalLayout height={'auto'} paddingLeft={36} paddingRight={36}>
            <TitleLargeMedium>관리자 계정 관리</TitleLargeMedium>
            <BTBox height='36px' />
            <BTList
                rows={rows}
                columns={columns}
                checkboxSelection={true}
                handleRowClick={handleRowClick}
                onWriteButtonClick={handleWriteClick}
                onDeleteButtonClick={handleDeleteClick}
                selectedRows={selectedRows} 
                setSelectedRows={setSelectedRows}
                pageStateKey="account.list"
            />
        </VerticalLayout>
    );
});

